import { Button, Input, Label } from '@betstarters/ui'
import { useTranslation } from 'react-i18next'
import { ErrorBanner } from './catalog'
import { Select } from './controls'
import type { ReferenceItem } from '../hooks/useReference'

export interface PaymentMethodFormValue {
  paymentMethodTypeId: string
  paymentMethod: string
  description: string
  logoPath: string
  icon: string
}

export const emptyPaymentMethodForm: PaymentMethodFormValue = {
  paymentMethodTypeId: '',
  paymentMethod: '',
  description: '',
  logoPath: '',
  icon: '',
}

export function isPaymentMethodFormValid(v: PaymentMethodFormValue): boolean {
  return v.paymentMethod.trim() !== '' && v.paymentMethodTypeId !== ''
}

/**
 * The create/edit form for a payment method, shared by the list page's dialog
 * and the detail page's summary tab — one definition of the fields, their
 * lengths and their labels.
 */
export function PaymentMethodForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  types,
  pending,
  error,
  submitLabel,
  autoFocus = true,
}: {
  value: PaymentMethodFormValue
  onChange: (v: PaymentMethodFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  types: ReferenceItem[]
  pending: boolean
  error: string
  submitLabel: string
  autoFocus?: boolean
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<PaymentMethodFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isPaymentMethodFormValid(value)) onSubmit()
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pm-type">{t('paymentMethods.columns.type')}</Label>
        <Select
          value={value.paymentMethodTypeId}
          onValueChange={(v) => set({ paymentMethodTypeId: v })}
          items={types.map((pt) => ({ value: String(pt.id), label: pt.name }))}
          className="w-full"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pm-name">{t('paymentMethods.columns.name')}</Label>
        <Input
          id="pm-name"
          value={value.paymentMethod}
          onChange={(e) => set({ paymentMethod: e.target.value })}
          maxLength={100}
          placeholder="CRYPTO.BITCOIN"
          autoFocus={autoFocus}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pm-description">{t('paymentMethods.columns.description')}</Label>
        <Input
          id="pm-description"
          value={value.description}
          onChange={(e) => set({ description: e.target.value })}
          maxLength={1000}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pm-logo">{t('paymentMethods.columns.logo')}</Label>
        <Input
          id="pm-logo"
          value={value.logoPath}
          onChange={(e) => set({ logoPath: e.target.value })}
          maxLength={1000}
          placeholder="/logos/btc.svg"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pm-icon">{t('paymentMethods.columns.icon')}</Label>
        <Input
          id="pm-icon"
          value={value.icon}
          onChange={(e) => set({ icon: e.target.value })}
          maxLength={100}
          placeholder="crypto-btc"
        />
      </div>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isPaymentMethodFormValid(value)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
